import { supabase } from '@/lib/supabase'
import type {
  AlertConfigRow,
  SystemFaultHistoryPoint,
  SystemFaultRow,
  SystemFaultStatus,
  SystemFaultsOverview,
} from '@/types'

const FAULT_ALERT_TYPES = [
  'fault_p0_new',
  'fault_volume_spike',
  'cron_failures',
  'security_advisor_new',
] as const

function emptyOverview(): SystemFaultsOverview {
  return {
    counts_by_severity: {},
    counts_by_status: {},
    open_faults: [],
    last_scan_at: null,
  }
}

export const systemFaultsApi = {
  faultAlertTypes: FAULT_ALERT_TYPES,

  overview: async (includeResolved = true): Promise<SystemFaultsOverview> => {
    try {
      const { data, error } = await supabase.rpc('system_faults_overview', {
        p_include_resolved: includeResolved,
        p_limit: 500,
      })
      if (error) throw error
      const raw = (data ?? {}) as Partial<SystemFaultsOverview>
      return {
        counts_by_severity: raw.counts_by_severity ?? {},
        counts_by_status: raw.counts_by_status ?? {},
        open_faults: (raw.open_faults ?? []) as SystemFaultRow[],
        last_scan_at: raw.last_scan_at ?? null,
      }
    } catch (e) {
      console.warn('[systemFaults] overview', e)
      return emptyOverview()
    }
  },

  historySeries: async (faultId: number, days = 1): Promise<SystemFaultHistoryPoint[]> => {
    try {
      const { data, error } = await supabase.rpc('system_faults_history_series', {
        p_fault_id: faultId,
        p_days: days,
      })
      if (error) throw error
      return (data ?? []) as SystemFaultHistoryPoint[]
    } catch (e) {
      console.warn('[systemFaults] historySeries', e)
      return []
    }
  },

  /**
   * Direct table update — requires platform-admin UPDATE grant (backend currently
   * SELECT-only; see result.md). UI gates with isSuperAdmin.
   */
  updateFault: async (
    id: number,
    patch: Partial<Pick<SystemFaultRow, 'status' | 'notes' | 'acknowledged_by' | 'resolved_at'>>,
  ): Promise<void> => {
    const { error } = await supabase.from('system_faults').update(patch).eq('id', id)
    if (error) throw error
  },

  acknowledge: async (id: number, userId: string): Promise<void> => {
    await systemFaultsApi.updateFault(id, {
      status: 'acknowledged' as SystemFaultStatus,
      acknowledged_by: userId,
    })
  },

  mute: async (id: number, notes: string): Promise<void> => {
    await systemFaultsApi.updateFault(id, {
      status: 'muted' as SystemFaultStatus,
      notes,
    })
  },

  resolve: async (id: number): Promise<void> => {
    await systemFaultsApi.updateFault(id, {
      status: 'resolved' as SystemFaultStatus,
      resolved_at: new Date().toISOString(),
    })
  },

  saveNotes: async (id: number, notes: string): Promise<void> => {
    await systemFaultsApi.updateFault(id, { notes })
  },

  listFaultAlertConfig: async (): Promise<AlertConfigRow[]> => {
    try {
      const { data, error } = await supabase
        .from('alert_config')
        .select('alert_type, threshold, channel, recipients, cooldown_secs, quiet_hours, enabled')
        .in('alert_type', [...FAULT_ALERT_TYPES])
      if (error) throw error
      return (data ?? []) as AlertConfigRow[]
    } catch (e) {
      console.warn('[systemFaults] alert_config', e)
      return []
    }
  },

  updateAlertConfig: async (
    alertType: string,
    patch: Partial<Pick<AlertConfigRow, 'threshold' | 'recipients' | 'enabled'>>,
  ): Promise<void> => {
    const { error } = await supabase.from('alert_config').update(patch).eq('alert_type', alertType)
    if (error) throw error
  },
}
